import React, { useState } from 'react';
import { format } from 'date-fns';

const PRESET_NOTE_TAGS = [
  'Customer contacted via phone',
  'Waiting on customer reply',
  'Vendor support ticket raised',
  'Issue under active investigation',
  'Follow-up scheduled',
  'Internal workaround deployed'
];

const TicketNoteModal = ({
  isOpen,
  ticket,
  onClose,
  onAddNote,
  isLoading = false
}) => {
  const [noteContent, setNoteContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen || !ticket) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!noteContent.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const res = await onAddNote(ticket.id, noteContent.trim());
      if (res) {
        setNoteContent('');
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const existingNotes = Array.isArray(ticket.notes) ? ticket.notes : [];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="fixed inset-0" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden z-10 animate-fadeIn text-slate-800">
        
        {/* Clean White Header */}
        <div className="px-5 py-3.5 border-b border-slate-200 bg-white text-slate-900 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center border border-blue-100 shadow-2xs flex-shrink-0">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm sm:text-base font-bold text-slate-900 tracking-tight">Add Note to Ticket</h3>
              <p className="text-[11px] text-slate-500 font-mono">
                #{ticket.id?.slice(0, 8)} • {ticket.title}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-800 flex items-center justify-center text-xs transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4 text-xs max-h-[80vh] overflow-y-auto">
          
          {/* Target Ticket Context Card */}
          <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/80 flex items-center justify-between">
            <div className="min-w-0 pr-2">
              <span className="font-bold text-slate-900 block truncate text-xs">{ticket.title}</span>
              <span className="text-[10px] text-slate-500">
                Created by {ticket.created_by_username || 'User'} • Priority: <span className="font-bold capitalize text-slate-700">{ticket.priority}</span>
              </span>
            </div>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold capitalize flex-shrink-0 ${
              ticket.status === 'open' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
              ticket.status === 'in_progress' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
              'bg-emerald-50 text-emerald-700 border border-emerald-200'
            }`}>
              {ticket.status?.replace('_', ' ')}
            </span>
          </div>

          {/* Existing Notes Thread (if any) */}
          {existingNotes.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                  Previous Notes ({existingNotes.length})
                </span>
              </div>
              <div className="space-y-2 max-h-44 overflow-y-auto p-2 bg-slate-50/70 rounded-xl border border-slate-200/60">
                {existingNotes.map((note) => (
                  <div key={note.id} className="p-2.5 rounded-lg bg-white border border-slate-200/80 shadow-2xs space-y-1">
                    <div className="flex items-center justify-between text-[10px]">
                      <div className="flex items-center gap-1.5 font-bold text-slate-800">
                        <span>{note.created_by_username || 'Agent'}</span>
                        {note.created_by_role && (
                          <span className="px-1.5 py-0.2 rounded bg-blue-50 text-blue-700 font-semibold text-[9px] uppercase">
                            {note.created_by_role}
                          </span>
                        )}
                      </div>
                      <span className="text-slate-400 text-[10px]">
                        {note.created_at ? format(new Date(note.created_at), 'MMM dd, HH:mm') : 'Recent'}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-700 leading-relaxed whitespace-pre-wrap">
                      {note.content}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Preset Tags */}
          <div>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1.5">
              Quick Tags:
            </span>
            <div className="flex flex-wrap gap-1.5">
              {PRESET_NOTE_TAGS.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setNoteContent(prev => prev ? `${prev}\n• ${tag}` : `• ${tag}`)}
                  className="px-2.5 py-1 bg-slate-100 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200 text-slate-600 border border-slate-200/80 rounded-lg text-[10px] font-medium transition-colors"
                >
                  +{tag}
                </button>
              ))}
            </div>
          </div>

          {/* Note Input */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">
              Internal Note / Update Note <span className="text-rose-500">*</span>
            </label>
            <textarea
              rows={3}
              required
              placeholder="Type your note, customer update, or diagnostic observation..."
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 focus:bg-white focus:border-blue-500 rounded-xl p-3 text-xs text-slate-800 focus:outline-none leading-relaxed transition-all"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || isLoading || !noteContent.trim()}
              className="px-4 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 rounded-xl transition-all shadow-xs flex items-center gap-1.5"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {isSubmitting || isLoading ? 'Saving...' : 'Save Note'}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
};

export default TicketNoteModal;
